import { Controller } from 'stimulus'

export default class CalculadoraCuotasHipoteca extends Controller {
  static targets = ['ahorroInput', 'ahorroRange', 'coberturaAhorroWarn', 'cuotaCalculadaInput', 'estadoInmuebleSelect',
                    'finalidadSelect', 'form', 'formWrapper', 'gastosCompraWarn', 'importeSolicitadoInput', 'maxFinanciacionWarn',
                    'plazoInput', 'provinciaSelect', 'tasacionInput', 'tipoInteresNumberInput', 'tipoInteresRadio', 'toggler']

  ahorroValue = 0
  cuotaMensual = 0
  impuestoIvaOItp = 0
  impuestoIajd = 0
  interesAnual = 0
  maxFinanciacionIndex = 0
  maxAhorroPrevisto = 0
  minAhorroPrevisto = 0
  numeroCuotas = 0
  precioCompraOTasacionValue = 0
  timers = new Object
  totalGastosCompra = 0

  initialize () {
    if (window.frameElement) {
      window.frameElement.dataset.autoheight = 'disabled'
      this.formWrapperTarget.style.height = '200'
      this.formWrapperTarget.overflowY = 'hidden'
    }
    this.hideAllTipoDeInteresValues()
    this.setupEventListeners()
    this.recalculateAndDisplayValues()
  }

  connect () {
    if (window.frameElement) {
      this.expandForm = window.frameElement ? () => {
        this.togglerTarget.style.display = 'none'
        this.formWrapperTarget.style.height = ''
        this.formWrapperTarget.overflowY = ''
        window.frameElement.dataset.autoheight = 'enabled'
        this.element.closest('[is="iframeable-autoheight"]').resetIframeHeight()
      } : () => {}
    }
  }

  get ahorroNoLlegaAlMinimoPrevisto () {
    return this.ahorroValue < this.minAhorroPrevisto
  }

  get coberturaAhorroWarnHTML () {
    const cubreGastosONo = this.ahorroValue < this.minAhorroPrevisto ? 'no cubrirías gastos' : 'cubrirías gastos'
    const textoInicio = `Con <b>${this.numberToCurrency(this.ahorroValue)}</b> ${cubreGastosONo}`
    const porcentaje = ((this.ahorroValue - this.totalGastosCompra) / this.precioCompraOTasacionValue * 100)
    const textoFin = 'del precio del inmueble'

    if (porcentaje > 0) {
      return `${textoInicio} y un <b>${porcentaje.toFixed()}%</b> ${textoFin}.`
    } else {
      return `${textoInicio}, y necesitarías financiar el 100% ${textoFin}.`
    }
  }

  get gastosCompraWarnHTML () {
    return `Los gastos de la compra serán aproximadamente ${this.numberToCurrency(this.totalGastosCompra)},
           impuestos incluidos, basados en el precio, el estado del inmueble y la localización.`
  }

  get isSegundaMano () {
    return this.estadoInmuebleSelectTarget.value === 'segunda_mano'
  }

  get isSegundaVivienda () {
    return this.finalidadSelectTarget.value === 'segunda_vivienda'
  }

  get finalidadElegidaText () {
    return this.finalidadSelectTarget.selectedOptions[0].textContent.toLowerCase()
  }

  get maxFinanciacionWarnHTML () {
    return `Para ${this.finalidadElegidaText} la financiación máxima suele ser
           del ${this.maxFinanciacionIndex * 100}% del precio de compra.`
  }

  get plazoNoLlegaAlMinimoPrevisto () {
    return this.plazoInputTarget.valueAsNumber < 1
  }

  get precioNoLlegaAlMinimoPrevisto () {
    return this.precioCompraOTasacionValue < this.tasacionInputTarget.minAsNumber
  }

  get provinciaElegida () {
    return this.provinciaSelectTarget.selectedOptionValue ? this.provinciaSelectTarget.selectedOptionText : null
  }

  get tipoInteresElegido () {
    return this.formTarget.querySelector('input[name$="[tipo_interes]"]:checked').value
  }

  get totalAFinanciar () {
    return this.importeSolicitadoInputTarget.value
  }

  set totalAFinanciar (value) {
    this.importeSolicitadoInputTarget.value = value
  }

  // The mother of the lamb!
  calculateCuotaMensual () {
    if (this.interesAnual === 0) {
      return this.totalAFinanciar / this.numeroCuotas
    } else {
      const interesMensual = (this.interesAnual / 100.0) / 12
      const multiplicador = Math.pow(1.0 + interesMensual, this.numeroCuotas)

      return this.totalAFinanciar * ((interesMensual * multiplicador) / (multiplicador - 1.0))
    }
  }

  calculateGastosCompra () {
    const notaria = 875.0
    const registro = 650.0
    const gestoria = 300.0
    const tasador = 350.0

    return Math.ceil((this.impuestoIvaOItp + this.impuestoIajd + notaria + registro + gestoria + tasador) / 100) * 100
  }

  // Impuestos Actos Jurídicos Documentados
  calculateIajd () {
    let indice = 0.005 // 0,5% del importe de la hipoteca

    if (this.provinciaElegida) {
      indice = json_impuestos_por_provincia[this.provinciaElegida].iajd.replace('%', '') / 100
    }

    return this.precioCompraOTasacionValue * indice
  }

  // Impuestos Actos Jurídicos Documentados
  calculateItp () {
    let indice = 0.08 // ITP MEDIO del 8%

    if (this.provinciaElegida) {
      indice = json_impuestos_por_provincia[this.provinciaElegida].itp.replace('%', '') / 100
    }

    return this.precioCompraOTasacionValue * indice // 0,5% del importe de la hipoteca
  }

  calculateIva () {
    return this.precioCompraOTasacionValue * 0.1 // IVA del 10%
  }

  hideAllTipoDeInteresValues () {
    this.tipoInteresNumberInputTargets.forEach(input => {
      input.stepper.style.display = 'none'
    })
  }

  normalizeNumericInput (input, callback) {
    input.value = Number(input.value)

    if (input.value.length > input.max.length) {
      input.value = input.value.slice(0, input.max.length)
    }

    if (typeof callback === 'function') {
      callback.bind(this)()
    }

    if (this.timers.hasOwnProperty(input.id)) {
      clearTimeout(this.timers[input.id])
    }

    this.timers[input.id] = setTimeout(() => {
      if (input.value.length === 0 || input.valueAsNumber < 0) {
        input.value = 0
        input.dispatchEvent(new CustomEvent('input'))
      }
    }, 1_000)
  }

  numberToCurrency (number) {
    if (window.Intl === undefined) {
      return String(Math.round(number))
    }

    const options = {
      style: 'currency',
      currency: 'EUR',
      useGrouping: true,
      maximumFractionDigits: 0,
      minimumFractionDigits: 0
    }

    return Intl.NumberFormat(navigator.language, options).format(number)
  }

  obtainMainValues () {
    this.precioCompraOTasacionValue = this.tasacionInputTarget.valueAsNumber
    this.impuestoIvaOItp = this.isSegundaMano ? this.calculateItp() : this.calculateIva()
    this.impuestoIajd = this.calculateIajd()
    this.totalGastosCompra = this.precioCompraOTasacionValue > 0 ? this.calculateGastosCompra() : 0
    this.maxFinanciacionIndex = this.isSegundaVivienda ? 0.7 : 0.9
    this.minAhorroPrevisto = this.totalGastosCompra
    this.maxAhorroPrevisto = this.precioCompraOTasacionValue
    this.ahorroValue = this.precioCompraOTasacionValue > 0 ? this.ahorroInputTarget.valueAsNumber : 0
    this.numeroCuotas = (12 * this.plazoInputTarget.valueAsNumber) || 1
    this.interesAnual = this.formTarget.querySelector(`#${this.tipoInteresElegido}-input-number`).valueAsNumber
    this.totalAFinanciar = this.precioCompraOTasacionValue - this.ahorroValue + this.totalGastosCompra
    this.cuotaMensual = this.calculateCuotaMensual()
  }

  onSubmit () {
    this.formTarget.button.textContent = 'Enviando...'

    if (window.frameElement) {
      this.onSubmitWhenIframed()
    } else {
      this.formTarget.submit()
      this.resetButtonWithDelay()
    }
  }

  onSubmitInvalid () {
    this.formTarget.button.textContent = 'Revisa los campos'
    this.resetButtonWithDelay()
  }

  onSubmitWhenIframed () {
    const url = this.formTarget.action
    const data = new FormData(this.formTarget)

    fetch(url, { method: 'post', headers: { 'Accept': 'application/json' }, body: data })
    .then(response => {
      return response.ok ? response.json() : { button_text: 'Hubo un error' }
    }).then(json => {
      this.formTarget.button.textContent = json.button_text
      this.resetButtonWithDelay()
    })
  }

  recalculateAndDisplayValues () {
    this.obtainMainValues()
    this.updateDependentInputs()
    this.renderCalculatedStuff()
  }

  renderCalculatedStuff () {
    const cuotaFormateada = this.numberToCurrency(this.cuotaMensual)

    this.cuotaCalculadaInputTarget.style.width = `${cuotaFormateada.length}ch`
    this.cuotaCalculadaInputTarget.value = cuotaFormateada

    this.warnOutline(this.tasacionInputTarget.stepper, this.precioNoLlegaAlMinimoPrevisto)
    this.warnOutline(this.ahorroInputTarget.stepper, this.ahorroNoLlegaAlMinimoPrevisto)
    this.warnOutline(this.plazoInputTarget.stepper, this.plazoNoLlegaAlMinimoPrevisto)

    this.gastosCompraWarnTarget.innerHTML = this.gastosCompraWarnHTML
    this.maxFinanciacionWarnTarget.innerHTML = this.maxFinanciacionWarnHTML
    this.coberturaAhorroWarnTarget.innerHTML = this.coberturaAhorroWarnHTML
  }

  resetButtonWithDelay () {
    setTimeout(() => {
      this.formTarget.button.textContent = 'Enviar'
      this.formTarget.button.disabled = false
    }, 1000)
  }

  setupEventListeners () {
    this.tipoInteresRadioTargets.forEach(inputRadio => {
      const padre = inputRadio.closest(`#${inputRadio.value}-inputs`)
      const inputNumerico = padre.querySelector(`#${inputRadio.value}-input-number`)

      inputNumerico.setAttribute('disabled', true)

      const mostrarTipoInteresInputElegido = () => {
        if (inputRadio.checked) {
          inputNumerico.stepper.style.display = ''
          inputNumerico.removeAttribute('disabled')
        }
      }

      mostrarTipoInteresInputElegido()

      inputRadio.addEventListener('input', () => {
        // delete all checked radio
        this.tipoInteresRadioTargets.forEach(inputRadio => {
          inputRadio.removeAttribute('checked')
        })
        // add checked radio to checked one
        inputRadio.setAttribute('checked', true)
        this.hideAllTipoDeInteresValues()
        mostrarTipoInteresInputElegido()
      })
    })

    this.formTarget.querySelectorAll('input, select').forEach(input => {
      input.addEventListener('input', () => {
        if (input === this.tasacionInputTarget) {
          this.normalizeNumericInput(this.tasacionInputTarget, () => {
            this.recalculateAndDisplayValues()
            if (this.ahorroInputTarget.valueAsNumber < this.minAhorroPrevisto) {
              this.ahorroInputTarget.value = this.minAhorroPrevisto
              this.ahorroInputTarget.dispatchEvent(new CustomEvent('input'))
            }
          })
        } else if (input === this.ahorroInputTarget) {
          this.normalizeNumericInput(this.ahorroInputTarget, this.recalculateAndDisplayValues)
        } else if (input === this.plazoInputTarget) {
          this.normalizeNumericInput(this.plazoInputTarget, this.recalculateAndDisplayValues)
        } else if (input.matches('select')) {
          this.recalculateAndDisplayValues()
          this.warnOutline(input.newSelect, !input.selectedOptionValue)
        } else {
          if (input.matches('[type="number"]') && input.value === '') {
            input.value = 0
            input.setAttribute('value', 0)
          }
          this.recalculateAndDisplayValues()
        }
      })
    })
  }

  submit (event) {
    event.preventDefault()
    this.formTarget.button.textContent = '...'
    this.formTarget.button.disabled = true

    if (this.formTarget.checkValidity()) {
      this.onSubmit()
    } else {
      this.formTarget.querySelectorAll('select[is="select-custom"][required]').forEach(select => {
        this.warnOutline(select.newSelect, !select.value)
      })
      this.onSubmitInvalid()
    }
  }

  updateDependentInputs () {
    this.ahorroValue = Math.min(this.ahorroValue, this.maxAhorroPrevisto)

    this.ahorroInputTarget.value = this.ahorroValue
    this.ahorroInputTarget.min = 0
    this.ahorroInputTarget.max = this.maxAhorroPrevisto

    this.ahorroRangeTarget.value = this.ahorroInputTarget.value
    this.ahorroRangeTarget.min = this.ahorroInputTarget.min
    this.ahorroRangeTarget.max = this.ahorroInputTarget.max
  }

  warnOutline (element, condition) {
    element.style.outline = condition ? '2px solid orange' : ''
  }
}
